import { WizardSteps } from './state-helpers';

import type { WizardStepName } from './state-helpers';
import type { InitialWizardState } from '../../types';
import type { ConfigureStepState } from '../state/configure';
import type { DetailsStepState } from '../state/details';
import type { OnlineDetailsStepState } from '../state/online-details';
import type { RequirementsStepState } from '../state/requirements';
import type { RewardsStepState } from '../state/rewards';

// Re-export types and constants
export { WizardSteps, type WizardStepName } from './state-helpers';
export type { ProductResource } from './product-matching';

// Re-export all helper functions
export * from './product-matching';
export * from './filter-helpers';
export * from './requirement-helpers';
export * from './state-helpers';

// Generic builder options
export type BuildDiscountInitialStateOptions = {
  /** Configure step configuration */
  configure?: Partial<ConfigureStepState> & { visited?: boolean };
  /** Details step configuration */
  details?: Partial<DetailsStepState> & { visited?: boolean };
  /** Online details step configuration */
  onlineDetails?: Partial<OnlineDetailsStepState> & { visited?: boolean };
  /** Step to open wizard on (defaults to CONFIGURE) */
  openOnStep?: WizardStepName;
  /** Requirements step configuration - can specify requirements by index (e.g., 1: createRequirement(...)) */
  requirements?: Partial<RequirementsStepState>;
  /** Rewards step configuration */
  rewards?: Partial<RewardsStepState> & { visited?: boolean };
};

/**
 * Generic builder for creating initial wizard state with any configuration.
 *
 * @example
 * // Simple example - just set a name and open on rewards step
 * const state = buildDiscountInitialState({
 *   openOnStep: 'REWARDS',
 *   configure: { discountName: 'My Discount', visited: true },
 * });
 *
 * @example
 * // Complex example - pre-fill multiple steps with products
 * const state = buildDiscountInitialState({
 *   openOnStep: 'REWARDS',
 *   configure: {
 *     discountName: 'Holiday Sale',
 *     visited: true
 *   },
 *   details: {
 *     startDate: new Date(),
 *     endDate: new Date('2024-12-31'),
 *     visited: true
 *   },
 *   requirements: {
 *     visited: true,
 *     1: createRequirement({
 *       filters: createRequirementFilters({
 *         product: createProductFilter(products)
 *       })
 *     })
 *   }
 * });
 */
export function buildDiscountInitialState(options: BuildDiscountInitialStateOptions = {}): InitialWizardState {
  const { openOnStep = 'CONFIGURE', configure, details, requirements, rewards, onlineDetails } = options;

  const state: InitialWizardState = {
    defaultStepToOpenOn: WizardSteps[openOnStep],
  };

  if (configure) {
    state.configureStep = configure;
  }

  if (details) {
    state.detailsStep = details;
  }

  if (requirements) {
    state.requirementsStep = requirements;
  }

  if (rewards) {
    state.rewardsStep = rewards;
  }

  if (onlineDetails) {
    state.onlineDetailsStep = onlineDetails;
  }

  return state;
}
